import React, { useContext, useState, SubmitEvent, MouseEvent } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTextDomainContext } from "@/app/providers";
import { useLogin } from "@/hooks";
import { trim } from "lodash";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const { VITE_RECAPTCHA_SITE_KEY } = import.meta.env;

interface LoginPayload {
  email: string;
  password: string;
  rememberMe: boolean;
  captcha?: string;
}

/**
 * LoginForm Component
 */
export const LoginForm: React.FC = (): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // reCAPTCHA site key comes from environment var VITE_RECAPTCHA_SITE_KEY
  // When absent, captcha is skipped to keep existing behaviour.
  const siteKey = VITE_RECAPTCHA_SITE_KEY;

  // Use reCAPTCHA v3 hook - must be called unconditionally at top level
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { submit, isSubmitting } = useLogin(
    () => {
      setErrorMessage("");
      setInfoMessage(gettext("Login successful. Redirecting."));
    },
    (error: string | null) => {
      if (error) {
        setErrorMessage(error);
      } else {
        setErrorMessage(gettext("Unknown error Occurred !"));
      }
      setInfoMessage("");
    },
  );

  const handleClickShowPassword = (): void => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
  };

  const handleSubmit = async (
    event: SubmitEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const emailInput = formElement.elements.namedItem(
      "email",
    ) as HTMLInputElement | null;
    const passwordInput = formElement.elements.namedItem(
      "password",
    ) as HTMLInputElement | null;

    const email = trim(emailInput?.value || "");
    const password = trim(passwordInput?.value || "");

    if (!email && !password) {
      setErrorMessage(gettext("Please enter a valid email and password!"));
    } else if (!email) {
      setErrorMessage(gettext("Please enter a valid email!"));
    } else if (!password) {
      setErrorMessage(gettext("Please enter a valid password!"));
    } else {
      setErrorMessage("");
      setInfoMessage(gettext("Validating. Please wait."));

      let captcha: string | null = null;
      if (siteKey && executeRecaptcha) {
        try {
          captcha = await executeRecaptcha("login");
        } catch {
          setErrorMessage(
            gettext("CAPTCHA verification failed. Please try again."),
          );
          setInfoMessage("");
          return;
        }
      } else if (siteKey && !executeRecaptcha) {
        // This shouldn't happen because the button is disabled, but just in case
        setInfoMessage(
          gettext("Please wait while security verification loads..."),
        );
        return;
      }

      const payload: LoginPayload = {
        email,
        password,
        rememberMe: true,
      };

      if (captcha) {
        payload.captcha = captcha;
      }

      submit(payload);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid
        container
        spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        <Grid size={{ xs: 12 }}>
          <Typography variant="h2" sx={{ color: "grey.900" }}>
            {gettext("Sign in")}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <FormControl fullWidth>
            <OutlinedInput
              id="email-login"
              type="email"
              name="email"
              size="medium"
              placeholder={gettext("Email address")}
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <FormControl fullWidth>
            <OutlinedInput
              id="password-login"
              name="password"
              type={showPassword ? "text" : "password"}
              size="medium"
              placeholder={gettext("Password")}
              endAdornment={
                <InputAdornment
                  position="end"
                  sx={{
                    color: "#D4D4D8",
                  }}
                >
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    color="inherit"
                    size="small"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 8 }}>
          <FormControl fullWidth>
            <Button
              disableElevation
              disabled={isSubmitting || (siteKey && !executeRecaptcha)}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              sx={{ color: "primary" }}
            >
              {gettext("Sign in")}
            </Button>
          </FormControl>
        </Grid>

        {siteKey && !executeRecaptcha && (
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              {gettext("Loading security verification...")}
            </Typography>
          </Grid>
        )}

        {(errorMessage || infoMessage) && (
          <Grid size={{ xs: 12, sm: 8 }}>
            {infoMessage && <Alert severity="info">{infoMessage}</Alert>}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
